import { Helmet } from "react-helmet-async";

// Use the hero image as the default OG image
const DEFAULT_OG_IMAGE = "https://innateperspectives.com/images/og-home.jpg";
const SITE_URL = "https://innateperspectives.com";
const DEFAULT_IMAGE_ALT = "Innate Perspectives - Stress & Capacity Architecture";

/**
 * SEO Component for managing page-specific meta tags
 * Optimized for both traditional SEO and Answer Engine Optimization (AEO)
 * Includes complete image metadata for social sharing (LinkedIn, Facebook, Twitter, WhatsApp, iMessage)
 */
export default function SEO({ 
  title = "Innate Perspectives", 
  description = "Discover your stress decision pattern. When pressure rises, you don't become irrational, you become automatic. Map your pattern. Master your response.",
  path = "",
  image = DEFAULT_OG_IMAGE,
  imageAlt = DEFAULT_IMAGE_ALT,
  imageWidth = "1536",
  imageHeight = "1024",
  type = "website",
  keywords = "stress decision architecture, capacity architecture, leadership coaching, stress response patterns, decision making under pressure, stress assessment, Brooklyn Eller"
}) {
  const fullUrl = `${SITE_URL}${path}`;
  // If the caller already provided a composed title (containing "|" or the brand),
  // use it verbatim so we can hit the 60-char SERP limit without double-branding.
  let fullTitle;
  if (title === "Innate Perspectives") {
    fullTitle = "Innate Perspectives | Stress & Capacity Architecture";
  } else if (title.includes("|") || title.includes("Innate Perspectives")) {
    fullTitle = title;
  } else {
    fullTitle = `${title} | Innate Perspectives`;
  }

  return (
    <Helmet>
      {/* Page-specific tags only, constant tags (keywords, author, robots, twitter:card, og:type, og:site_name, og:locale) are set statically in index.html to avoid duplicate-tag warnings */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />

      {/* Open Graph - page-specific */}
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:alt" content={imageAlt} />
      <meta property="og:image:width" content={imageWidth} />
      <meta property="og:image:height" content={imageHeight} />

      {/* Twitter - page-specific */}
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={imageAlt} />

      {/* Canonical URL */}
      <link rel="canonical" href={fullUrl} />
    </Helmet>
  );
}

/**
 * Comprehensive Schema.org markup for better AEO (Answer Engine Optimization)
 * Includes Organization, LocalBusiness, Service, Person, and FAQ schemas
 */
export function SchemaMarkup() {
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Innate Perspectives",
    "url": SITE_URL,
    "logo": "https://customer-assets.emergentagent.com/job_birth-ready/artifacts/p71eeafq_tree%20blue%20.png",
    "description": "Stress & Capacity Architecture. Helping people understand their stress decision patterns and master their response under pressure.",
    "founder": {
      "@type": "Person",
      "name": "Brooklyn Eller"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+61404065064",
      "contactType": "customer service",
      "email": "hello@innateperspectives.com",
      "availableLanguage": "English"
    },
    "sameAs": [
      "https://g.page/r/CV6i5bsnxCG0EBM"
    ]
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "name": "Innate Perspectives",
    "description": "Stress & Capacity Architecture. Discover your stress decision pattern, how you automatically respond when pressure rises. Map your pattern. Master your response.",
    "url": SITE_URL,
    "telephone": "+61404065064",
    "email": "hello@innateperspectives.com",
    "priceRange": "$47 - $12600",
    "image": `${SITE_URL}/images/og-home.jpg`,
    "founder": {
      "@type": "Person",
      "name": "Brooklyn Eller"
    },
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "AU",
      "addressRegion": "NSW"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "-33.8688",
      "longitude": "151.2093"
    },
    "areaServed": [
      { "@type": "Country", "name": "Australia" },
      { "@type": "AdministrativeArea", "name": "Worldwide (online)" }
    ],
    "serviceType": [
      "Executive stress coaching",
      "Leadership decision architecture",
      "Capacity coaching",
      "Stress response assessment"
    ],
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "5.0",
      "reviewCount": "16",
      "bestRating": "5",
      "worstRating": "1"
    },
    "review": [
      {
        "@type": "Review",
        "author": { "@type": "Person", "name": "Jessica C." },
        "reviewRating": { "@type": "Rating", "ratingValue": "5" },
        "reviewBody": "I had my first session with Brooklyn today & words couldn't really describe the session. I had zero expectations & was truly amazed by the movement I felt emotionally and mentally."
      },
      {
        "@type": "Review",
        "author": { "@type": "Person", "name": "Tommy A." },
        "reviewRating": { "@type": "Rating", "ratingValue": "5" },
        "reviewBody": "Brooklyn helped myself and my Wife by uncovering what lied underneath the surface, this is an experience like no other. I feel truly blessed to come across such a caring person who takes a scientific approach to mental and physical health."
      },
      {
        "@type": "Review",
        "author": { "@type": "Person", "name": "Dominique O." },
        "reviewRating": { "@type": "Rating", "ratingValue": "5" },
        "reviewBody": "I came across Brooklyn by chance, but sometimes chance meetings are meant to happen. It is difficult for me to describe exactly what took place, you have to just TRUST in the process."
      }
    ]
  };

  const personSchema = {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": "Brooklyn Eller",
    "url": `${SITE_URL}/smallbusiness`,
    "image": `${SITE_URL}/images/be/brooklyn-headshot.jpg`,
    "jobTitle": "Stress & Capacity Architecture Specialist",
    "description": "Founder of Innate Perspectives. Specialist in stress decision architecture, helping leaders, founders, and high-performers identify and rewire the automatic patterns driving their decisions under pressure.",
    "worksFor": {
      "@type": "Organization",
      "name": "Innate Perspectives",
      "url": SITE_URL
    },
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "AU"
    },
    "knowsAbout": [
      "Stress Decision Architecture",
      "Capacity Architecture",
      "Executive Coaching",
      "Leadership Development",
      "Stress Response Patterns",
      "Decision Making Under Pressure",
      "Nervous System Regulation",
      "Neuro Change Methods",
      "Cognitive Behavioural Therapy (CBT)",
      "Neuro-Linguistic Programming (NLP)",
      "Psych-K",
      "Kolbe Methodology"
    ],
    "hasCredential": [
      { "@type": "EducationalOccupationalCredential", "credentialCategory": "certification", "name": "Kolbe Certified Consultant" },
      { "@type": "EducationalOccupationalCredential", "credentialCategory": "certification", "name": "Neuro Change Methods Practitioner" },
      { "@type": "EducationalOccupationalCredential", "credentialCategory": "certification", "name": "CBT Practitioner" },
      { "@type": "EducationalOccupationalCredential", "credentialCategory": "certification", "name": "NLP Practitioner" },
      { "@type": "EducationalOccupationalCredential", "credentialCategory": "certification", "name": "Psych-K Facilitator" }
    ]
  };

  const servicesSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Innate Perspectives Services",
    "itemListElement": [
      {
        "@type": "Service",
        "position": 1,
        "name": "Stress Decision Architecture Assessment",
        "description": "Discover your stress decision archetype, how you automatically respond when pressure rises. Free assessment with detailed results.",
        "provider": { "@type": "Organization", "name": "Innate Perspectives" },
        "serviceType": "Assessment"
      },
      {
        "@type": "Service",
        "position": 2,
        "name": "Core Archetype Report",
        "description": "Deep-dive analysis of your stress decision pattern with specific strategies for regulation across all life domains.",
        "provider": { "@type": "Organization", "name": "Innate Perspectives" },
        "serviceType": "Digital Report"
      },
      {
        "@type": "Service",
        "position": 3,
        "name": "Focused Session with Brooklyn",
        "description": "45-minute session to work through a specific situation, decision, or pattern using your archetype profile.",
        "provider": { "@type": "Organization", "name": "Innate Perspectives" },
        "serviceType": "Coaching Session"
      },
      {
        "@type": "Service",
        "position": 4,
        "name": "Stress Decision Architecture Workshop",
        "description": "Three 90-minute executive sessions helping teams map stress structures and install conditions that stabilise decision clarity under load.",
        "provider": { "@type": "Organization", "name": "Innate Perspectives" },
        "serviceType": "Executive Workshop"
      }
    ]
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What is Stress Decision Architecture?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Stress Decision Architecture is a framework for understanding how you automatically respond when pressure rises. When stress activates, you don't become irrational, you become automatic. This assessment maps that automatic pattern so you can master your response."
        }
      },
      {
        "@type": "Question",
        "name": "What are the five stress decision archetypes?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "The five archetypes are: The Visionary Architect (big-picture thinking), The Analyst (thorough research), The Strategist (systematic organization), The Igniter (quick action), and The Anchor (careful evaluation). Each represents a distinct pattern of how people respond under pressure."
        }
      },
      {
        "@type": "Question",
        "name": "How is this different from personality tests?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "This assessment focuses specifically on how you respond under stress and pressure, not your general personality. It maps the automatic patterns that emerge when stakes are high, in leadership, relationships, money decisions, conflict, and major life transitions."
        }
      },
      {
        "@type": "Question",
        "name": "Who is Brooklyn Eller?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Brooklyn Eller is the founder of Innate Perspectives and a specialist in stress decision architecture. She helps leaders and individuals understand their stress patterns and develop capacity to remain present and self-directed under pressure."
        }
      }
    ]
  };

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Innate Perspectives",
    "url": SITE_URL,
    "description": "Stress & Capacity Architecture. Discover your stress decision pattern and master your response under pressure.",
    "potentialAction": {
      "@type": "SearchAction",
      "target": `${SITE_URL}/search?q={search_term_string}`,
      "query-input": "required name=search_term_string"
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(localBusinessSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(personSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(servicesSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(faqSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>
    </Helmet>
  );
}

/**
 * Page-specific SEO configurations with complete image metadata
 */
export const PAGE_SEO = {
  home: {
    title: "Innate Perspectives",
    description: "Discover your stress decision pattern. When pressure rises, you don't become irrational, you become automatic. Map your pattern. Master your response.",
    keywords: "stress decision architecture, capacity architecture, stress assessment, leadership coaching, decision making under pressure, Brooklyn Eller",
    imageAlt: "Innate Perspectives - Stress & Capacity Architecture"
  },
  quiz: {
    title: "Stress Decision Assessment",
    description: "Take the free stress decision assessment. Discover which of the 5 archetypes you are and how your pattern shows up in leadership, love, money, conflict, and more.",
    keywords: "stress assessment, stress decision quiz, archetype quiz, stress patterns, free assessment",
    imageAlt: "Free Stress Decision Assessment"
  },
  results: {
    title: "Your Stress Decision Archetype",
    description: "Your stress decision pattern revealed. See how this archetype shapes your decisions in leadership, relationships, money, conflict, parenting, and major transitions.",
    keywords: "stress archetype results, stress decision pattern, archetype report",
    imageAlt: "Your Stress Decision Archetype Results"
  },
  leadership: {
    title: "Leadership Coaching",
    description: "Executive coaching for leaders who face high-stakes decisions. Understand your stress patterns and strengthen your capacity to remain present under pressure.",
    keywords: "leadership coaching, executive coaching, decision making, stress management, leadership development, high performance",
    imageAlt: "Leadership Coaching - Stress & Capacity Architecture"
  },
  leadershipServices: {
    title: "Leadership Services",
    description: "Individual sessions, intensive programs, and executive retreats designed to recalibrate how you meet pressure. Find the right entry point for your leadership journey.",
    keywords: "executive coaching services, leadership programs, intensive coaching, executive retreat, leadership development",
    imageAlt: "Leadership coaching services - Individual sessions, intensives, and retreats"
  },
  leadershipAbout: {
    title: "About Brooklyn Eller | Stress Decision Architecture™",
    description: "Meet Brooklyn Eller, founder of Innate Perspectives. 20+ years integrating Kolbe, Neuro Change Methods, CBT, NLP, and Psych-K into Stress Decision Architecture™ for executives and founders.",
    keywords: "Brooklyn Eller, executive coach Australia, stress decision architecture, Kolbe certified, neuro change methods, leadership coach founder",
    imageAlt: "Brooklyn Eller, founder of Innate Perspectives, executive stress and decision-making coach"
  },
  leadershipIndividual: {
    title: "Individual Leadership Session",
    description: "A single focused session for leaders facing a defined moment of pressure. Understand your stress pattern and develop strategies for clarity and presence.",
    keywords: "individual coaching session, executive session, leadership coaching, single session coaching",
    imageAlt: "Individual Leadership Session - Focused coaching for high-stakes moments"
  },
  leadershipTenSession: {
    title: "Ten Session Leadership Program",
    description: "A sustained engagement for leaders navigating ongoing pressure. Build lasting internal capacity across multiple dimensions of leadership.",
    keywords: "leadership program, ten session coaching, executive development, sustained coaching",
    imageAlt: "Ten Session Leadership Program - Sustained executive development"
  },
  leadershipYearLong: {
    title: "Leadership Stability Structure",
    description: "Ongoing decision support for leaders with continuous responsibility. 40 sessions to maintain clean responses as circumstances change.",
    keywords: "ongoing leadership support, executive coaching program, leadership stability, comprehensive coaching",
    imageAlt: "Leadership Stability Structure - Ongoing executive decision support"
  },
  leadershipRetreat: {
    title: "Executive Leadership Retreat",
    description: "An immersive retreat experience for leaders seeking intensive recalibration. Step away from daily demands to reset your internal foundation.",
    keywords: "executive retreat, leadership retreat, immersive coaching, intensive leadership program",
    imageAlt: "Executive Leadership Retreat - Immersive recalibration experience"
  },
  faq: {
    title: "Frequently Asked Questions",
    description: "Common questions about stress decision architecture, the archetype assessment, and how this work differs from traditional coaching.",
    keywords: "stress decision FAQ, archetype questions, assessment FAQ, innate perspectives FAQ",
    imageAlt: "Frequently Asked Questions about Innate Perspectives"
  },
  privacyPolicy: {
    title: "Privacy Policy",
    description: "Learn how Innate Perspectives collects, uses, and protects your personal information. Our commitment to your privacy and data security.",
    keywords: "privacy policy, data protection, personal information, GDPR compliance",
    imageAlt: "Innate Perspectives Privacy Policy"
  },
  terms: {
    title: "Terms and Conditions",
    description: "Terms and conditions governing the use of Innate Perspectives services. Please review before engaging with our coaching programs.",
    keywords: "terms and conditions, service agreement, coaching terms, legal terms",
    imageAlt: "Innate Perspectives Terms and Conditions"
  },
  cookies: {
    title: "Cookies Policy",
    description: "Information about how Innate Perspectives uses cookies and similar technologies on our website.",
    keywords: "cookies policy, cookie usage, website cookies, tracking technologies",
    imageAlt: "Innate Perspectives Cookies Policy"
  },
  disclaimer: {
    title: "Disclaimer",
    description: "Important disclaimers regarding the nature of coaching services provided by Innate Perspectives.",
    keywords: "disclaimer, coaching disclaimer, service limitations, legal disclaimer",
    imageAlt: "Innate Perspectives Disclaimer"
  },
  accessibility: {
    title: "Accessibility Statement",
    description: "Our commitment to making Innate Perspectives accessible to all users. Information about accessibility features and accommodations.",
    keywords: "accessibility, accessible website, accommodations, inclusive design",
    imageAlt: "Innate Perspectives Accessibility Statement"
  },
  informedConsent: {
    title: "Informed Consent",
    description: "Information about informed consent for coaching services. Understanding what to expect from your engagement with Innate Perspectives.",
    keywords: "informed consent, coaching consent, client agreement, service consent",
    imageAlt: "Innate Perspectives Informed Consent"
  },
  workshops: {
    title: "Stress Decision Architecture Workshop",
    description: "Three 90-minute executive sessions helping teams map stress structures and install conditions that stabilise decision clarity under load.",
    keywords: "executive workshop, team coaching, stress decision architecture, leadership workshop, team development",
    imageAlt: "Stress Decision Architecture Workshop for executive teams"
  },
  assessment: {
    title: "Stress Decision Architecture Assessment",
    description: "Discover your stress decision pattern with our free assessment. Understand how you operate under pressure and identify areas for growth.",
    keywords: "stress assessment, leadership assessment, decision making assessment, free archetype quiz",
    imageAlt: "Stress Decision Architecture Assessment"
  },
  blog: {
    title: "Insights Blog",
    description: "Articles on stress decision patterns, capacity architecture, and staying self-directed when stakes are high.",
    keywords: "stress decision blog, capacity insights, decision making articles, leadership development",
    imageAlt: "Innate Perspectives Insights Blog"
  }
};

/**
 * Dynamic FAQ Schema Generator for FAQPage
 * Pass an array of {question, answer} objects to generate structured data
 */
export function FAQSchema({ faqs }) {
  if (!faqs || faqs.length === 0) return null;
  
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(faqSchema)}
      </script>
    </Helmet>
  );
}

/**
 * Dynamic Breadcrumb Schema Generator
 * Pass an array of {name, url} objects for the breadcrumb trail
 */
export function BreadcrumbSchema({ items }) {
  if (!items || items.length === 0) return null;
  
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>
    </Helmet>
  );
}

/**
 * Service Schema Generator for individual service pages
 */
export function ServiceSchema({ 
  name, 
  description, 
  price, 
  priceCurrency = "AUD",
  duration,
  serviceType = "Executive Coaching"
}) {
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": name,
    "description": description,
    "provider": {
      "@type": "Organization",
      "name": "Innate Perspectives",
      "url": SITE_URL
    },
    "serviceType": serviceType,
    ...(price && {
      "offers": {
        "@type": "Offer",
        "price": price,
        "priceCurrency": priceCurrency
      }
    }),
    ...(duration && { "duration": duration })
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(serviceSchema)}
      </script>
    </Helmet>
  );
}
